import { AppointmentFilters, emptyAppointmentFilters, filterAppointments } from "../../components/AppointmentFilters";
import { ScheduleFollowupModal } from "../../components/ScheduleFollowupModal";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, EmptyState, CommonModal } from "../../components/Common";
import api from "../../lib/api";
import { doctorOffersService, effectiveBookingPrice, normalizeBookingService, normalizeDoctorServiceAssignment } from "../../lib/bookingOptions";
import { toast } from "sonner";
import { User, Activity, FileText, Pill, FilePlus, CreditCard, ActivityIcon, Phone, FileStack, Plus, ChevronLeft, Settings, History, MapPin, Upload, Download, Trash2, AlertTriangle, CalendarClock, Stethoscope } from "lucide-react";
import Video from "lucide-react/dist/esm/icons/video";
import { useCan } from "../../lib/permissions";
import { useAuth } from "../../lib/auth";
import { PatientTagPicker } from "../clinicadmin/PatientsModulePages";
import { FollowupConfirmModal, FollowupConvertModal } from "../../components/FollowupConvertModal";

export function Patient360Page({ homeBase, links, apiBase = "/clinic/patients-x" }) {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { user } = useAuth();
  const id = routeId || "me";
  
  const [patient, setPatient] = useState(null);
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data lists
  const [appointments, setAppointments] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  
  const [vitalModal, setVitalModal] = useState(false);
  const [noteModal, setNoteModal] = useState(false);
  const [apptModal, setApptModal] = useState(false);
  const [followupModal, setFollowupModal] = useState(false);
  const [convertFollowup, setConvertFollowup] = useState(null);
  const [confirmFollowup, setConfirmFollowup] = useState(null);
  const [encounterModal, setEncounterModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  
  const isSuperAdmin = user.role === "super_admin" && !user.clinic_id;

  const canManageTags = useCan("patients.edit");
  const [savingTags, setSavingTags] = useState(false);
  const saveTags = async (tag_ids) => {
    if(savingTags)return; setSavingTags(true);
    try { const {data}=await api.put(`/clinic/patients-x/tag-assignments/${patient.patient_id}`,{tag_ids,source:"patient_edit"}); setPatient(p=>({...p,tag_ids,tags:data.tags||[]})); toast.success("Patient tags saved"); }
    catch(e){toast.error(e?.response?.data?.detail||"Patient tags could not be saved");}
    finally{setSavingTags(false);}
  };
  const canViewMedical = useCan("patients.view_medical_history") || user.role === "doctor" || user.role === "patient";
  const canViewBilling = useCan("billing.view") || user.role === "patient";
  const canViewTimeline = useCan("patients.view_timeline") || user.role === "doctor" || user.role === "patient";
  const canAddVitals = useCan("patients.add_vitals") && !isSuperAdmin;
  const canAddNotes = useCan("patients.add_notes") && !isSuperAdmin;
  const canViewNotes = useCan("patients.view_notes") || user.role === "doctor";
  const canAddAppt = useCan("appointments.create") && !isSuperAdmin;
  const canAddFollowup = useCan("followups.create") && !isSuperAdmin;
  const canConvertFollowup = useCan("followups.convert_to_appointment") && !isSuperAdmin;
  const canCreateEncounter = useCan("encounters.create") && !isSuperAdmin;
  const canEditPatient = useCan("patients.edit") || isSuperAdmin;
  const canViewClinicDocuments = useCan("patients.view_documents");
  const canViewPortalDocuments = useCan("patient_portal.view_documents");
  const canViewDocuments = canViewClinicDocuments || canViewPortalDocuments || isSuperAdmin;
  const canUploadDocuments = useCan("patients.upload_documents") && !isSuperAdmin;
  const canDeleteDocuments = useCan("patients.delete_documents") && !isSuperAdmin;

  const isPatientPortal = user.role === "patient";

  const fetchPatient = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${apiBase}/${id}`);
      setPatient(res.data);
      try { setClinicalSummary((await api.get(`${apiBase}/${id}/clinical-summary`)).data); }
      catch { setClinicalSummary(null); }
    } catch (e) {
      toast.error("Failed to load patient details");
      if (!isPatientPortal) navigate(`${homeBase}/patients`);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTabData = async (tab, force = false) => {
    if (!id) return;
    try {
      if (tab === "appointments" && (force || appointments.length === 0)) {
        const r = await api.get(`${apiBase}/${id}/appointments`);
        setAppointments(r.data);
      }
      if (tab === "encounters" && (force || encounters.length === 0) && canViewMedical) {
        const r = await api.get(`${apiBase}/${id}/encounters`);
        setEncounters(r.data);
      }
      if (tab === "prescriptions" && (force || prescriptions.length === 0) && canViewMedical) {
        const r = await api.get(`${apiBase}/${id}/prescriptions`);
        setPrescriptions(r.data);
      }
      if (tab === "followups" && (force || followups.length === 0)) {
        const r = await api.get(`${apiBase}/${id}/followups`);
        setFollowups(r.data);
      }
      if (tab === "billing" && (force || invoices.length === 0) && canViewBilling) {
        const r = await api.get(`${apiBase}/${id}/invoices`);
        setInvoices(r.data);
      }
      if (tab === "vitals" && (force || vitals.length === 0) && canViewMedical && !isPatientPortal) {
        const r = await api.get(`${apiBase}/${id}/vitals`);
        setVitals(r.data);
      }
      if (tab === "timeline" && (force || timeline.length === 0) && canViewTimeline) {
        const r = await api.get(`${apiBase}/${id}/timeline`);
        setTimeline(r.data);
      }
      if (tab === "notes" && (force || notes.length === 0) && canViewNotes && !isPatientPortal) {
        const r = await api.get(`${apiBase}/${id}/notes`);
        setNotes(r.data);
      }
      if (tab === "documents" && canViewDocuments && (force || documents.length === 0)) {
        const r = await api.get(`${apiBase}/${id}/documents`);
        setDocuments(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "This section could not be loaded. Please try again.");
    }
  };

  useEffect(() => {
    setActiveTab("overview");setAppointments([]);setEncounters([]);setPrescriptions([]);setFollowups([]);setInvoices([]);setVitals([]);setDocuments([]);setTimeline([]);setNotes([]);
    fetchPatient();
    // Patient identity is the only state transition that requires a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshDocuments = async () => {
    try {
      const r = await api.get(`${apiBase}/${id}/documents`);
      setDocuments(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to refresh documents");
    }
  };

  const handleTabChange = (t) => {
    setActiveTab(t);
    fetchTabData(t, true);
  };
  
  if (loading) return <DashboardLayout links={links} title="Patient Details"><div className="p-8 text-center text-slate-500">Loading patient data...</div></DashboardLayout>;
  if (!patient) return <DashboardLayout links={links} title="Not Found"><EmptyState title="Patient not found" /></DashboardLayout>;

  return (
    <DashboardLayout links={links} title={isPatientPortal ? "My Health Record" : "Patient 360°"}>
      
      {!isPatientPortal && (
        <div className="mb-4">
          <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-mg-navy flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        </div>
      )}

      {/* Patient Header Card */}
      <div className="mg-card mb-6 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="w-24 h-24 rounded-full mg-gradient-primary flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
          {patient.name?.charAt(0) || "P"}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-mg-navy">{patient.name}</h1>
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-mono">ID: {patient.patient_id?.includes("_") ? patient.patient_id.split("_").slice(1).join("_") : patient.patient_id}</span>
            {patient.blood_group && <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-full font-bold">Blood: {patient.blood_group}</span>}
            <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">{patient.status || "Active"}</span>
            {isSuperAdmin && patient.clinic_records?.length > 1 && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-semibold">{patient.clinic_records.length} clinic records</span>}
            {patient.stats?.pending_amount > 0 && canViewBilling && (
               <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full font-semibold border border-orange-200">Due: ₹{patient.stats.pending_amount}</span>
            )}
            {!isPatientPortal && patient.tags?.map(tag => <span key={tag.tag_id} className="text-xs px-2 py-1 rounded-full text-white font-semibold" style={{backgroundColor: tag.color}}>{tag.display_name}</span>)}
          </div>
          
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1.5"><ActivityIcon className="w-4 h-4 text-slate-400" /> {patient.age ? `${patient.age} yrs, ` : ""}{patient.gender || "Unknown"}</div>
            <div className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" /> {patient.phone || "—"}</div>
            <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {patient.city || patient.address || "—"}</div>
            <div className="flex items-center gap-1.5 text-xs">Registered: {new Date(patient.created_at).toLocaleDateString()}</div>
          </div>
          {canViewMedical && (patient.allergies || patient.allergies_json) && <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"><AlertTriangle className="w-4 h-4" /> Allergy alert: {patient.allergies || patient.allergies_json}</div>}
          {canEditPatient && (
            <button onClick={() => setEditModal(true)} className="mt-3 text-xs text-mg-medical hover:underline font-semibold">
              Edit Profile
            </button>
          )}
        </div>
        
        {/* Quick Actions */}
        {!isPatientPortal && (
          <div className="flex flex-col gap-2 min-w-[140px]">
            {canAddAppt && <button onClick={() => setApptModal(true)} className="mg-btn-primary text-xs w-full"><Plus className="w-3 h-3" /> Appointment</button>}
            {canAddFollowup && <button onClick={() => setFollowupModal(true)} className="mg-btn-secondary text-xs w-full"><Plus className="w-3 h-3" /> Follow-up</button>}
            {canViewMedical && canCreateEncounter && <button onClick={() => setEncounterModal(true)} className="mg-btn-secondary text-xs w-full text-mg-medical border-mg-medical hover:bg-blue-50">Start Encounter</button>}
          </div>
        )}
      </div>

      {!isPatientPortal && <section className="mg-card mb-5" aria-label="Patient tags"><h2 className="font-bold mb-3">Patient tags</h2>{canManageTags?<fieldset disabled={savingTags}><PatientTagPicker value={patient.tag_ids||[]} onChange={saveTags}/></fieldset>:<p>{(patient.tags||[]).map(t=>t.display_name).join(", ")||"No tags assigned"}</p>}</section>}
      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200 mb-6 custom-scrollbar">
        <TabBtn label="Overview" active={activeTab === "overview"} onClick={() => handleTabChange("overview")} icon={<User />} />
        <TabBtn label="Appointments" active={activeTab === "appointments"} onClick={() => handleTabChange("appointments")} icon={<History />} />
        {canViewMedical && !isPatientPortal && <TabBtn label="Encounters" active={activeTab === "encounters"} onClick={() => handleTabChange("encounters")} icon={<FilePlus />} />}
        {canViewMedical && <TabBtn label="Prescriptions" active={activeTab === "prescriptions"} onClick={() => handleTabChange("prescriptions")} icon={<Pill />} />}
        <TabBtn label="Follow-ups" active={activeTab === "followups"} onClick={() => handleTabChange("followups")} icon={<Activity />} />
        {canViewBilling && <TabBtn label="Billing" active={activeTab === "billing"} onClick={() => handleTabChange("billing")} icon={<CreditCard />} />}
        {canViewDocuments && <TabBtn label="Documents" active={activeTab === "documents"} onClick={() => handleTabChange("documents")} icon={<FileStack />} />}
        {!isPatientPortal && canViewMedical && <TabBtn label="Vitals" active={activeTab === "vitals"} onClick={() => handleTabChange("vitals")} icon={<ActivityIcon />} />}
        {canViewTimeline && <TabBtn label="Timeline" active={activeTab === "timeline"} onClick={() => handleTabChange("timeline")} icon={<History />} />}
        {!isPatientPortal && canViewNotes && <TabBtn label="Notes" active={activeTab === "notes"} onClick={() => handleTabChange("notes")} icon={<FileText />} />}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === "overview" && <TabOverview patient={patient} summary={clinicalSummary} canViewMedical={canViewMedical} showTags={false} />}
        {activeTab === "appointments" && <TabAppointments appointments={appointments} />}
        {!isPatientPortal && activeTab === "encounters" && <TabEncounters encounters={encounters} homeBase={homeBase} />}
        {activeTab === "prescriptions" && <TabPrescriptions prescriptions={prescriptions} />}
        {activeTab === "followups" && <TabFollowups followups={followups} canConvert={canConvertFollowup} onPrepare={(followup) => followup.status?.toLowerCase() === "confirmed_pending_booking" ? setConvertFollowup(followup) : setConfirmFollowup(followup)} />}
        {activeTab === "billing" && <TabBilling invoices={invoices} patient={patient} />}
        {activeTab === "vitals" && <TabVitals vitals={vitals} onAdd={() => setVitalModal(true)} canAdd={canAddVitals} />}
        {activeTab === "documents" && <TabDocuments documents={documents} patientId={id} apiBase={apiBase} canUpload={canUploadDocuments} canDelete={canDeleteDocuments} onRefresh={refreshDocuments} />}
        {activeTab === "timeline" && <TabTimeline timeline={timeline} />}
        {activeTab === "notes" && <TabNotes notes={notes} onAdd={() => setNoteModal(true)} canAdd={canAddNotes} />}
      </div>

      {vitalModal && <AddVitalModal onClose={() => setVitalModal(false)} patientId={patient.patient_id} onSave={() => { setVitalModal(false); fetchTabData("vitals", true); fetchPatient(); }} apiBase={apiBase} />}
      {noteModal && <AddNoteModal onClose={() => setNoteModal(false)} patientId={patient.patient_id} onSave={() => { setNoteModal(false); fetchTabData("notes", true); }} apiBase={apiBase} />}
      {apptModal && <BookApptModal onClose={() => setApptModal(false)} patient={patient} onSave={() => { setApptModal(false); fetchTabData("appointments", true); fetchPatient(); }} />}
      {followupModal && <AddFollowupModal onClose={() => setFollowupModal(false)} patient={patient} onSave={() => { setFollowupModal(false); fetchTabData("followups", true); fetchTabData("appointments", true); fetchPatient(); }} />}
      {convertFollowup && <FollowupConvertModal followupId={convertFollowup.followup_id} doctorId={convertFollowup.doctor_id} clinicId={convertFollowup.clinic_id || patient.clinic_id} initialDate={convertFollowup.due_date || convertFollowup.recommended_date} onClose={() => setConvertFollowup(null)} onConverted={() => { setConvertFollowup(null); fetchTabData("followups", true); fetchTabData("appointments", true); fetchPatient(); }} />}
      {confirmFollowup && <FollowupConfirmModal followupId={confirmFollowup.followup_id} onClose={() => setConfirmFollowup(null)} onConfirmed={() => { const followup = confirmFollowup; setConfirmFollowup(null); setConvertFollowup({ ...followup, status: "confirmed_pending_booking" }); fetchTabData("followups", true); }} />}
      {editModal && <EditPatientModal onClose={() => setEditModal(false)} patient={patient} onSave={() => { setEditModal(false); fetchPatient(); }} apiBase={apiBase} />}
      {encounterModal && <StartEncounterModal onClose={() => setEncounterModal(false)} patient={patient} homeBase={homeBase} />}

    </DashboardLayout>
  );
}

function TabBtn({ label, active, onClick, icon }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors whitespace-nowrap ${active ? "bg-mg-medical text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {React.cloneElement(icon, { className: "w-4 h-4" })} {label}
    </button>
  );
}

// ---------------- TAB COMPONENTS ----------------

function TabOverview({ patient, summary, canViewMedical, showTags }) {
  const fmt = (value) => value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";
  const medications = summary?.current_medications || [];
  const chains = summary?.active_chains || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {canViewMedical && (
          <>
            <div className="mg-card border-t-4 border-mg-medical">
              <h3 className="font-bold text-mg-navy mb-4 flex items-center gap-2"><Stethoscope className="w-5 h-5 text-mg-medical" /> Clinical snapshot</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <ClinicalValue label="Allergies" value={patient.allergies || patient.allergies_json || "None recorded"} alert={Boolean(patient.allergies || patient.allergies_json)} />
                <ClinicalValue label="Chronic conditions" value={patient.chronic_conditions_json || "None recorded"} />
                <ClinicalValue label="Last diagnosis" value={summary?.last_encounter?.diagnosis || "No completed encounter"} />
                <ClinicalValue label="Latest vitals" value={summary?.latest_vitals ? [summary.latest_vitals.blood_pressure && `BP ${summary.latest_vitals.blood_pressure}`, summary.latest_vitals.pulse && `Pulse ${summary.latest_vitals.pulse}`, summary.latest_vitals.weight && `${summary.latest_vitals.weight} kg`].filter(Boolean).join(" · ") || "Recorded" : "No vitals recorded"} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="mg-card"><h3 className="font-bold text-mg-navy mb-3">Active treatment chains</h3>{chains.length ? <div className="space-y-3">{chains.map(chain => <div key={chain.chain_id} className="rounded-lg bg-slate-50 p-3"><div className="font-semibold text-mg-navy">{chain.title}</div><div className="text-xs text-slate-500 mt-1">{chain.main_problem || "Problem not specified"}</div></div>)}</div> : <p className="text-sm text-slate-500">No active treatment chain.</p>}</div>
              <div className="mg-card"><h3 className="font-bold text-mg-navy mb-3">Current medication</h3>{medications.length ? <div className="space-y-2">{medications.map((item, i) => <div key={`${item.medicine_name || item.name}-${i}`} className="text-sm"><span className="font-semibold text-mg-navy">{item.medicine_name || item.name}</span><div className="text-xs text-slate-500">{[item.strength, item.dosage, item.frequency, item.duration].filter(Boolean).join(" · ")}</div></div>)}</div> : <p className="text-sm text-slate-500">No recent medication recorded.</p>}</div>
            </div>
            <div className="mg-card"><h3 className="font-bold text-mg-navy mb-3">Medical history</h3><p className="text-sm text-slate-700 whitespace-pre-wrap">{patient.medical_history || patient.medical_history_text || "No prior medical history available."}</p></div>
          </>
        )}
        <details className="mg-card" open={!canViewMedical}><summary className="font-bold text-mg-navy cursor-pointer flex items-center gap-2"><User className="w-4 h-4 text-mg-medical" /> Administrative details</summary><div className="grid grid-cols-2 gap-4 text-sm mt-4"><div><span className="block text-slate-500 text-xs">Email</span>{patient.email || "—"}</div><div><span className="block text-slate-500 text-xs">DOB</span>{patient.date_of_birth || "—"}</div><div><span className="block text-slate-500 text-xs">Emergency contact</span>{patient.emergency_contact_name ? `${patient.emergency_contact_name} (${patient.emergency_contact_phone})` : "—"}</div><div><span className="block text-slate-500 text-xs">Address</span>{patient.address || "—"}</div></div></details>
        {showTags && <div className="mg-card" data-testid="patient360-tags-section">
          <h3 className="font-bold text-mg-navy mb-3">Patient tags</h3>
          {patient.tags?.length ? <div className="flex flex-wrap gap-2">{patient.tags.map(tag => <span key={tag.tag_id} className="text-xs px-2.5 py-1 rounded-full text-white font-semibold" style={{backgroundColor: tag.color}}>{tag.display_name}</span>)}</div> : <p className="text-sm text-slate-500">No tags assigned.</p>}
        </div>}
      </div>

      <div className="space-y-6">
        <div className="mg-card"><h3 className="font-bold text-mg-navy mb-4 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-mg-medical" /> Care continuity</h3><div className="space-y-4 text-sm"><ClinicalValue label="Next appointment" value={fmt(summary?.next_appointment?.appointment_datetime)} /><ClinicalValue label="Pending follow-ups" value={String(summary?.pending_followups_count ?? patient.stats?.pending_followups ?? 0)} /><ClinicalValue label="Last visit" value={fmt(summary?.last_visit?.checked_in_at || summary?.last_visit?.created_at)} /><ClinicalValue label="Last encounter" value={fmt(summary?.last_encounter?.closed_at || summary?.last_encounter?.encounter_datetime)} /></div></div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-4">Patient Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-slate-600 text-sm">Total Appointments</span>
              <span className="font-bold text-mg-navy">{patient.stats?.total_appointments || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-slate-600 text-sm">Total Encounters</span>
              <span className="font-bold text-mg-navy">{patient.stats?.total_encounters || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-slate-600 text-sm">Pending Follow-ups</span>
              <span className="font-bold text-orange-600">{patient.stats?.pending_followups || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClinicalValue({ label, value, alert = false }) {
  return <div><span className="block text-slate-500 text-xs font-semibold mb-1">{label}</span><div className={alert ? "font-semibold text-red-700" : "font-medium text-slate-800"}>{value || "—"}</div></div>;
}

function TabAppointments({ appointments }) {
  const [filters,setFilters]=useState(emptyAppointmentFilters);
  if (!appointments?.length) return <EmptyState title="No appointments found" message="This patient has no appointment history." />;
  return (
    <div><AppointmentFilters rows={appointments} value={filters} onChange={setFilters}/><div className="mg-card p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
          <tr><th className="p-4">Date & Time</th><th>Doctor</th><th>Service</th><th>Status</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filterAppointments(appointments,filters).map((a) => (
            <tr key={a.appointment_id} className="hover:bg-slate-50">
              <td className="p-4 font-semibold">{new Date(a.appointment_datetime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
              <td>{a.doctor_name}</td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: a.service_color }} />
                  {a.service_name}
                </div>
              </td>
              <td>{a.clinic_name && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded block max-w-fit mb-1">{a.clinic_name}</span>}<span className={`mg-status mg-status-${a.status?.toLowerCase().replace(" ","-") || 'scheduled'}`}>{a.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}

function TabEncounters({ encounters, homeBase }) {
  const navigate = useNavigate();
  if (!encounters?.length) return <EmptyState title="No encounters found" message="No clinical encounters have been recorded yet." />;
  return (
    <div className="space-y-4">
      {encounters.map(e => (
        <div key={e.encounter_id} className="mg-card flex flex-col md:flex-row gap-4 justify-between items-start">
          <div>
            <div className="text-sm text-slate-500 font-semibold">{new Date(e.encounter_datetime || e.created_at).toLocaleDateString()} {e.clinic_name && <span className="ml-2 font-normal text-xs bg-slate-100 px-2 py-0.5 rounded">{e.clinic_name}</span>}</div>
            <h4 className="font-bold text-mg-navy text-lg mt-1">{e.chief_complaint || "Routine Consultation"}</h4>
            <div className="text-sm text-slate-600 mt-2"><span className="font-semibold text-slate-700">Diagnosis:</span> {e.diagnosis || "—"}</div>
            <div className="text-xs text-slate-400 mt-3 flex items-center gap-2">
               {e.doctor?.name && formatDoctorName(e.doctor.name)} <span className="w-1 h-1 rounded-full bg-slate-300"/> Status: {e.status}
            </div>
          </div>
          <button onClick={() => navigate(homeBase === "/doctor" ? `/doctor/encounter/${e.encounter_id}` : `${homeBase}/encounters/${e.encounter_id}`)} className="mg-btn-secondary text-sm">View Details</button>
        </div>
      ))}
    </div>
  );
}

function TabPrescriptions({ prescriptions }) {
  if (!prescriptions?.length) return <EmptyState title="No prescriptions" message="No prescriptions issued to this patient." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {prescriptions.map(r => (
        <div key={r.prescription_id} className="mg-card border-l-4 border-mg-medical">
          <div className="flex justify-between items-start mb-3">
             <div className="font-bold text-mg-navy">{new Date(r.created_at).toLocaleDateString()}</div>
             <div className="flex flex-col items-end gap-1">
               <span className="text-xs bg-slate-100 px-2 py-1 rounded">{formatDoctorName(r.doctor_name)}</span>
               {r.clinic_name && <span className="text-[10px] text-slate-400">{r.clinic_name}</span>}
             </div>
          </div>
          <div className="space-y-2">
            {(r.items || r.medicines || []).map((item, index) => (
              <div key={`${r.prescription_id}-${index}`} className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="font-semibold text-mg-navy">{item.medicine_name || item.name || "Medicine"}{item.strength ? ` ${item.strength}` : ""}</div>
                <div className="text-xs text-slate-600 mt-1">{[item.dosage, item.frequency, item.duration].filter(Boolean).join(" · ") || "Dosage not specified"}</div>
                {item.instructions && <div className="text-xs text-slate-500 mt-1">{item.instructions}</div>}
              </div>
            ))}
            {!(r.items || r.medicines || []).length && <div className="text-sm text-slate-400">No medication items recorded.</div>}
          </div>
          {r.notes && <div className="text-xs text-slate-500 mt-3 border-t pt-2">Notes: {r.notes}</div>}
        </div>
      ))}
    </div>
  );
}

function formatDoctorName(name) {
  if (!name || name === "Unknown") return "Doctor not assigned";
  return /^dr\.?\s/i.test(name) ? name : `Dr. ${name}`;
}

function TabFollowups({ followups, canConvert, onPrepare }) {
  if (!followups?.length) return <EmptyState title="No follow-ups" message="No follow-ups scheduled." />;
  return (
    <div className="mg-card p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
          <tr><th className="p-4">Due Date</th><th>Reason</th><th>Doctor</th><th>Status</th>{canConvert && <th className="text-right pr-4">Action</th>}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {followups.map(f => (
            <tr key={f.followup_id} className="hover:bg-slate-50">
              <td className="p-4 font-semibold text-mg-navy">{new Date(f.due_date || f.recommended_date).toLocaleDateString()} {f.clinic_name && <div className="text-[10px] text-slate-400 font-normal">{f.clinic_name}</div>}</td>
              <td>{f.reason}</td>
              <td>{f.doctor_name}</td>
              <td><span className={`mg-status mg-status-${f.status?.toLowerCase()}`}>{f.status}</span></td>
              {canConvert && <td className="text-right pr-4">{!["converted_to_appointment", "completed", "cancelled", "missed", "deleted"].includes(f.status?.toLowerCase()) && <button type="button" className="mg-btn-secondary text-xs" onClick={() => onPrepare(f)} data-testid={`patient360-followup-convert-${f.followup_id}`}>Convert to appointment</button>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabBilling({ invoices, patient }) {
  if (!invoices?.length) return <EmptyState title="No billing records" message="No invoices have been generated for this patient." />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
         <StatCard label="Total Billed" value={`₹${patient.stats?.total_billed || 0}`} />
         <StatCard label="Total Paid" value={`₹${patient.stats?.total_paid || 0}`} />
         <StatCard label="Pending Due" value={`₹${patient.stats?.pending_amount || 0}`} />
      </div>
      <div className="mg-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-4">Date</th><th>Invoice #</th><th className="text-right">Total</th><th className="text-right">Paid</th><th>Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map(i => (
              <tr key={i.invoice_id} className="hover:bg-slate-50">
                <td className="p-4">{new Date(i.invoice_date).toLocaleDateString()} {i.clinic_name && <div className="text-[10px] text-slate-400 font-normal">{i.clinic_name}</div>}</td>
                <td className="font-mono text-xs">{i.invoice_number}</td>
                <td className="text-right font-semibold">₹{i.total}</td>
                <td className="text-right text-mg-health">₹{i.paid_amount}</td>
                <td><span className={`mg-status mg-status-${i.status?.toLowerCase()}`}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabVitals({ vitals, onAdd, canAdd }) {
  return (
    <div className="space-y-4">
      {canAdd && (
        <div className="flex justify-end">
          <button onClick={onAdd} className="mg-btn-primary text-sm"><Plus className="w-4 h-4"/> Record Vitals</button>
        </div>
      )}
      {!vitals?.length ? <EmptyState title="No vitals recorded" /> : (
        <div className="mg-card p-0 overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 border-b text-xs text-slate-500 uppercase">
              <tr><th className="p-4">Date</th><th>BP</th><th>Pulse</th><th>Temp</th><th>Weight</th><th>Height</th><th>SpO₂</th><th>Respiration</th></tr>
            </thead>
            <tbody className="divide-y">
              {vitals.map(v => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="p-4">{new Date(v.recorded_at).toLocaleString([], {dateStyle:'short', timeStyle:'short'})}</td>
                  <td>{v.blood_pressure || "—"}</td>
                  <td>{v.pulse ? `${v.pulse} bpm` : "—"}</td>
                  <td>{v.temperature ? `${v.temperature}°` : "—"}</td>
                  <td>{v.weight ? `${v.weight} kg` : "—"}</td><td>{v.height ? `${v.height} cm` : "—"}</td><td>{v.spo2 ? `${v.spo2}%` : "—"}</td><td>{v.respiratory_rate ? `${v.respiratory_rate}/min` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabNotes({ notes, onAdd, canAdd }) {
  return (
    <div className="space-y-4">
      {canAdd && (
        <div className="flex justify-end">
          <button onClick={onAdd} className="mg-btn-primary text-sm"><Plus className="w-4 h-4"/> Add Note</button>
        </div>
      )}
      {!notes?.length ? <EmptyState title="No notes found" /> : (
        <div className="space-y-3">
          {notes.map(n => (
            <div key={n.id} className="mg-card flex flex-col">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold uppercase text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{n.note_type}</span>
                <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()} by {n.created_by_name}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.note_content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabTimeline({ timeline }) {
  if (!timeline?.length) return <EmptyState title="No timeline events" />;
  return (
    <div className="mg-card">
      <div className="relative border-l border-slate-200 ml-4 space-y-8 py-4">
        {timeline.map((ev, i) => (
          <div key={i} className="relative pl-6">
            <div className="absolute -left-2.5 top-1 w-5 h-5 bg-white border-2 border-mg-medical rounded-full shadow-sm" />
            <div className="font-bold text-mg-navy">{ev.title}</div>
            <div className="text-xs text-slate-400 mb-1">{new Date(ev.date).toLocaleString()}</div>
            <div className="text-sm text-slate-600">{ev.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabDocuments({ documents, patientId, apiBase, canUpload, canDelete, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: "", document_type: "lab_report", visibility: "patient_visible", notes: "", file: null });

  const download = async (document) => {
    try {
      const response = await api.get(`${apiBase}/${patientId}/documents/${document.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = document.original_filename || document.title || "document";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download document");
    }
  };

  const upload = async () => {
    if (!form.file || !form.title.trim()) {
      toast.error("Choose a file and enter a title");
      return;
    }
    const data = new FormData();
    data.append("file", form.file);
    data.append("title", form.title.trim());
    data.append("document_type", form.document_type);
    data.append("visibility", form.visibility);
    if (form.notes.trim()) data.append("notes", form.notes.trim());
    try {
      setUploading(true);
      await api.post(`${apiBase}/${patientId}/documents`, data);
      toast.success("Document uploaded");
      setShowUpload(false);
      setForm({ title: "", document_type: "lab_report", visibility: "patient_visible", notes: "", file: null });
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload document");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (document) => {
    if (!window.confirm(`Remove “${document.title}”? The audit record will be retained.`)) return;
    try {
      await api.delete(`${apiBase}/${patientId}/documents/${document.id}`);
      toast.success("Document removed");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove document");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div><h3 className="font-bold text-mg-navy">Documents</h3><p className="text-sm text-slate-500">Lab reports, imaging, referrals and other patient files.</p></div>
        {canUpload && <button className="mg-btn-primary text-sm" onClick={() => setShowUpload(true)}><Upload className="w-4 h-4" /> Upload document</button>}
      </div>
      {!documents?.length ? <EmptyState title="No documents yet" message={canUpload ? "Upload the first document for this patient." : "No documents have been shared with you."} /> : (
        <div className="mg-card divide-y divide-slate-100 p-0 overflow-hidden">
          {documents.map((document) => (
            <div key={document.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-mg-medical flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-mg-navy truncate">{document.title}</div>
                <div className="text-xs text-slate-500 mt-1">{(document.document_type || "other").replaceAll("_", " ")} · {document.original_filename || "File"} · {document.size_bytes ? `${(document.size_bytes / 1024).toFixed(document.size_bytes > 1024 * 1024 ? 0 : 1)} KB` : "Size unavailable"}</div>
                <div className="text-xs text-slate-400 mt-1">Uploaded {new Date(document.uploaded_at).toLocaleString()}{document.uploaded_by_name ? ` by ${document.uploaded_by_name}` : ""} · {document.visibility === "patient_visible" ? "Shared with patient" : document.visibility === "clinical" ? "Clinical team" : "Internal"}</div>
                {document.notes && <p className="text-sm text-slate-600 mt-2">{document.notes}</p>}
              </div>
              <div className="flex gap-2">
                <button className="mg-btn-secondary text-xs" onClick={() => download(document)} aria-label={`Download ${document.title}`}><Download className="w-4 h-4" /> Download</button>
                {canDelete && <button className="p-2 rounded-lg text-red-600 hover:bg-red-50" onClick={() => remove(document)} aria-label={`Remove ${document.title}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {showUpload && (
        <CommonModal title="Upload patient document" onClose={() => !uploading && setShowUpload(false)} onSave={upload} saveText={uploading ? "Uploading…" : "Upload"}>
          <div className="space-y-4">
            <div><label className="text-xs text-slate-500 mb-1 block">File (maximum 10 MB)</label><input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.docx,.xlsx" className="mg-input" onChange={e => setForm({...form, file: e.target.files?.[0] || null})} /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Title</label><input className="mg-input" maxLength={120} value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Complete blood count – August 2026" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs text-slate-500 mb-1 block">Type</label><select className="mg-input" value={form.document_type} onChange={e => setForm({...form, document_type: e.target.value})}><option value="lab_report">Lab report</option><option value="imaging">Imaging</option><option value="prescription">Prescription</option><option value="referral">Referral</option><option value="consent">Consent</option><option value="insurance">Insurance</option><option value="other">Other</option></select></div>
              <div><label className="text-xs text-slate-500 mb-1 block">Who can see it?</label><select className="mg-input" value={form.visibility} onChange={e => setForm({...form, visibility: e.target.value})}><option value="patient_visible">Clinical team and patient</option><option value="clinical">Clinical team only</option><option value="internal">Clinic staff only</option></select></div>
            </div>
            <div><label className="text-xs text-slate-500 mb-1 block">Notes (optional)</label><textarea className="mg-input min-h-20" maxLength={1000} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
        </CommonModal>
      )}
    </div>
  );
}


// --- Modals ---
function StartEncounterModal({ onClose, patient, homeBase }) {
  const [doctors, setDoctors] = useState([]);
  const [chains, setChains] = useState([]);
  const [requiresDecision, setRequiresDecision] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ doctor_id: "", chief_complaint: "", chain_decision: "", treatment_chain_id: "", chain_title: "" });
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/visits/options"), api.get(`/encounters/chain-options/${patient.patient_id}`)])
      .then(([options, chainOptions]) => {
        const availableDoctors = options.data.doctors || [];
        setDoctors(availableDoctors);
        setChains(chainOptions.data.chains || []);
        setRequiresDecision(Boolean(chainOptions.data.requires_decision));
        setForm(current => ({ ...current, doctor_id: availableDoctors.length === 1 ? availableDoctors[0].doctor_id : current.doctor_id, treatment_chain_id: chainOptions.data.suggested_chain_id || "", chain_decision: chainOptions.data.requires_decision ? "" : "no_chain" }));
      }).catch((error) => {
        // React development mode may cancel the first duplicate request while
        // remounting effects. Only surface genuine API failures to the user.
        if (error?.code !== "ERR_CANCELED" && error?.name !== "CanceledError") {
          toast.error("Encounter options could not be loaded");
        }
      });
  }, [patient.patient_id]);

  const start = async () => {
    if (!form.doctor_id) return toast.error("Choose a doctor");
    if (requiresDecision && !form.chain_decision) return toast.error("Choose continuing treatment or a new problem");
    if (form.chain_decision === "continue" && !form.treatment_chain_id) return toast.error("Choose a treatment chain");
    if (form.chain_decision === "start_new" && !form.chain_title.trim()) return toast.error("Enter a title for the new treatment chain");
    setBusy(true);
    try {
      const res = await api.post("/encounters", {
        patient_id: patient.patient_id,
        doctor_id: form.doctor_id,
        chief_complaint: form.chief_complaint,
        chain_decision: form.chain_decision,
        treatment_chain_id: form.chain_decision === "continue" ? form.treatment_chain_id : undefined,
        chain_title: form.chain_decision === "start_new" ? form.chain_title : undefined,
        chain_main_problem: form.chain_decision === "start_new" ? form.chief_complaint : undefined,
      });
      toast.success("Encounter Started");
      onClose();
      navigate(homeBase === "/doctor" ? `/doctor/encounter/${res.data.encounter_id}` : `${homeBase}/encounters/${res.data.encounter_id}`);
    } catch(e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to start encounter"); }
    finally { setBusy(false); }
  };

  return (
    <CommonModal title="Start Encounter" onClose={onClose} onSave={start} saveText={busy ? "Starting…" : "Start Encounter"}>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Doctor</label>
          <select className="mg-input w-full" value={form.doctor_id} onChange={e=>setForm({...form, doctor_id: e.target.value})}>
            <option value="">Select Doctor</option>
            {doctors.map(d => { const text = d.name || d.doctor_id; return <option key={d.doctor_id} value={d.doctor_id} label={text}>{text}</option>; })}
          </select>
        </div>
        {chains.length > 0 && <div className="space-y-2"><div className="text-sm font-semibold text-mg-navy">Is this continuing treatment or a new problem?</div><label className="flex gap-2 rounded-lg border p-3 text-sm"><input type="radio" checked={form.chain_decision === "continue"} onChange={() => setForm({...form, chain_decision:"continue"})} /> Continue existing treatment</label>{form.chain_decision === "continue" && <select className="mg-input w-full" value={form.treatment_chain_id} onChange={e=>setForm({...form, treatment_chain_id:e.target.value})}><option value="">Select treatment chain</option>{chains.map(chain => { const text = chain.title || chain.chain_id; return <option key={chain.chain_id} value={chain.chain_id} label={text}>{text}</option>; })}</select>}<label className="flex gap-2 rounded-lg border p-3 text-sm"><input type="radio" checked={form.chain_decision === "start_new"} onChange={() => setForm({...form, chain_decision:"start_new"})} /> Start a new problem</label></div>}
        {(form.chain_decision === "start_new" || !chains.length) && <div><label className="text-xs text-slate-500 mb-1 block">Treatment title {chains.length ? "" : "(optional)"}</label><input className="mg-input w-full" value={form.chain_title} onChange={e=>setForm({...form, chain_title:e.target.value, chain_decision:e.target.value ? "start_new" : "no_chain"})} placeholder="e.g. Back pain treatment" /></div>}
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Chief complaint</label>
          <input className="mg-input w-full" placeholder="E.g., fever, headache" value={form.chief_complaint} onChange={e=>setForm({...form, chief_complaint: e.target.value})} />
        </div>
      </div>
    </CommonModal>
  );
}

function AddVitalModal({ onClose, patientId, onSave, apiBase }) {
  const [form, setForm] = useState({ blood_pressure: "", pulse: "", temperature: "", weight: "" });
  const save = async () => {
    try {
      await api.post(`${apiBase}/${patientId}/vitals`, {
        blood_pressure: form.blood_pressure,
        pulse: form.pulse ? Number(form.pulse) : null,
        temperature: form.temperature ? Number(form.temperature) : null,
        weight: form.weight ? Number(form.weight) : null,
      });
      toast.success("Vitals added");
      onSave();
    } catch(e) { toast.error("Failed"); }
  };
  return (
    <CommonModal title="Record Vitals" onClose={onClose} onSave={save}>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-xs text-slate-500 block mb-1">Blood Pressure (e.g. 120/80)</label><input className="mg-input" value={form.blood_pressure} onChange={e=>setForm({...form, blood_pressure: e.target.value})} /></div>
        <div><label className="text-xs text-slate-500 block mb-1">Pulse (bpm)</label><input type="number" className="mg-input" value={form.pulse} onChange={e=>setForm({...form, pulse: e.target.value})} /></div>
        <div><label className="text-xs text-slate-500 block mb-1">Temperature (°F/C)</label><input type="number" className="mg-input" value={form.temperature} onChange={e=>setForm({...form, temperature: e.target.value})} /></div>
        <div><label className="text-xs text-slate-500 block mb-1">Weight (kg)</label><input type="number" className="mg-input" value={form.weight} onChange={e=>setForm({...form, weight: e.target.value})} /></div>
      </div>
    </CommonModal>
  );
}

function AddNoteModal({ onClose, patientId, onSave, apiBase }) {
  const [form, setForm] = useState({ note_type: "Clinical", note_content: "", visibility: "internal" });
  const save = async () => {
    try {
      await api.post(`${apiBase}/${patientId}/notes`, form);
      toast.success("Note added");
      onSave();
    } catch(e) { toast.error("Failed"); }
  };
  return (
    <CommonModal title="Add Note" onClose={onClose} onSave={save}>
      <div className="space-y-3">
        <select className="mg-input" value={form.note_type} onChange={e=>setForm({...form, note_type: e.target.value})}>
          <option>Clinical</option>
          <option>Administrative</option>
          <option>Billing</option>
        </select>
        <select className="mg-input" value={form.visibility} onChange={e=>setForm({...form, visibility: e.target.value})}>
          <option value="internal">Internal (Staff Only)</option>
          <option value="clinical">Clinical Only (Doctors)</option>
          <option value="patient_visible">Patient Visible</option>
        </select>
        <textarea className="mg-input min-h-[100px]" placeholder="Type your note here..." value={form.note_content} onChange={e=>setForm({...form, note_content: e.target.value})} />
      </div>
    </CommonModal>
  );
}


// --- Booking Modals ---
function BookApptModal({ onClose, patient, onSave }) {
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [doctorServices, setDoctorServices] = useState([]);
  const [form, setForm] = useState({ doctor_id: "", service_id: "", consultation_mode: "", date: new Date().toISOString().slice(0, 10), slot: null });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    api.get("/visits/options").then(({ data }) => {
      setDoctors(data.doctors || []);
      setServices((data.services || []).map(normalizeBookingService));
      setDoctorServices((data.doctor_services || []).map(normalizeDoctorServiceAssignment));
      if ((data.doctors || []).length === 1) {
        setForm(current => ({ ...current, doctor_id: data.doctors[0].doctor_id }));
      }
    }).catch((error) => toast.error(error?.response?.data?.detail || "Booking options could not be loaded"));
  }, []);

  const availableServices = form.doctor_id
    ? services.filter(service => doctorOffersService(doctorServices, form.doctor_id, service.service_id))
    : [];

  useEffect(() => {
    if (form.doctor_id && form.service_id && form.date) {
      let active = true;
      setSlots([]);
      setForm(current => ({ ...current, slot: null }));
      setLoadingSlots(true);
      api.get("/clinic/slots/available", { params: { doctor_id: form.doctor_id, service_id: form.service_id, date: form.date } })
         .then(r => { if (active) { setSlots(r.data.slots || r.data); setLoadingSlots(false); } })
         .catch(() => { if (active) { setSlots([]); setLoadingSlots(false); } });
      return () => { active = false; };
    } else {
      setSlots([]);
      setLoadingSlots(false);
    }
  }, [form.doctor_id, form.service_id, form.date]);

  const save = async () => {
    if (!form.slot) return toast.error("Please select a slot");
    try {
      await api.post("/appointments", {
        patient_id: patient.patient_id,
        doctor_id: form.doctor_id,
        service_id: form.service_id,
        appointment_datetime: form.slot.start || form.slot.start_time,
        consultation_mode: form.consultation_mode || null
      });
      toast.success("Appointment booked");
      onSave();
    } catch(e) { toast.error("Failed to book"); }
  };

  return (
    <CommonModal title="Book Appointment" onClose={onClose} onSave={save}>
      <div className="space-y-4">
        <div><label className="text-xs text-slate-500 mb-1 block">Patient</label><input className="mg-input bg-slate-50" value={patient.name} disabled /></div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Doctor</label>
            <select className="mg-input" value={form.doctor_id} onChange={e=>setForm({...form, doctor_id: e.target.value, service_id: "", consultation_mode: "", slot: null})}>
              <option value="">Select Doctor</option>
              {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>)}
            </select>
          </div>
          {availableServices.find((s)=>s.service_id===form.service_id)?.service_mode === "hybrid" && <div>
            <label className="text-xs text-slate-500 mb-1 block">Consultation type</label>
            <select className="mg-input" value={form.consultation_mode} onChange={e=>setForm({...form,consultation_mode:e.target.value})}><option value="">Choose</option><option value="in_person">In person</option><option value="online">Online</option></select>
          </div>}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Service</label>
            <select className="mg-input" value={form.service_id} onChange={e=>setForm({...form, service_id: e.target.value, consultation_mode: "", slot: null})} disabled={!form.doctor_id}>
              <option value="">Select Service</option>
              {availableServices.map(s => <option key={s.service_id} value={s.service_id}>{s.name} ({s.duration_minutes}m - ₹{effectiveBookingPrice(services, doctorServices, form.doctor_id, s.service_id)})</option>)}
            </select>
          </div>
        </div>
        <div>
           <label className="text-xs text-slate-500 mb-1 block">Date</label>
           <input type="date" className="mg-input" value={form.date} onChange={e=>setForm({...form, date: e.target.value, slot: null})} />
        </div>
        <div>
           <label className="text-xs text-slate-500 mb-2 block">Available Slots</label>
           {loadingSlots && <div className="text-xs text-slate-500">Loading...</div>}
           {!loadingSlots && slots.length === 0 && form.service_id && <div className="text-xs text-red-500">No slots available on this date.</div>}
           <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto custom-scrollbar">
              {!loadingSlots && slots.map((s, i) => (
                 <button key={i} onClick={() => setForm({...form, slot: s})} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.slot === s ? 'bg-mg-medical text-white border-mg-medical' : 'bg-white border-slate-200 text-slate-600 hover:border-mg-medical'}`}>
                   {new Date(s.start || s.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                 </button>
              ))}
           </div>
        </div>
      </div>
    </CommonModal>
  );
}

function AddFollowupModal({ onClose, patient, onSave }) {
  return <ScheduleFollowupModal patient={patient} onClose={onClose} onSaved={onSave} />;
}



function EditPatientModal({ patient, onClose, onSave, apiBase }) {
  const [form, setForm] = useState({
    name: patient.name || "",
    phone: patient.phone || "",
    email: patient.email || "",
    date_of_birth: patient.date_of_birth || "",
    gender: patient.gender || "",
    blood_group: patient.blood_group || "",
    address: patient.address || "",
    city: patient.city || "",
    emergency_contact_name: patient.emergency_contact_name || "",
    emergency_contact_phone: patient.emergency_contact_phone || "",
    allergies_json: patient.allergies_json || patient.allergies || "",
    risk_flags_json: patient.risk_flags_json || ""
  });
  const [tagIds, setTagIds] = useState(patient.tag_ids || []);

  const save = async () => {
    try {
      await api.patch(`${apiBase}/${patient.patient_id}`, form);
      if (apiBase.startsWith("/clinic/")) {
        await api.put(`/clinic/patients-x/tag-assignments/${patient.patient_id}`, { tag_ids: tagIds, source: "patient_edit" });
      }
      toast.success("Profile updated");
      onSave();
    } catch(e) { toast.error("Failed to update profile"); }
  };

  return (
    <CommonModal title="Edit Patient Profile" onClose={onClose} onSave={save}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Full Name</label>
          <input className="mg-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Phone</label>
          <input className="mg-input" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Email</label>
          <input type="email" className="mg-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Date of Birth</label>
          <input type="date" className="mg-input" value={form.date_of_birth} onChange={e=>setForm({...form, date_of_birth: e.target.value})} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Gender</label>
          <select className="mg-input" value={form.gender} onChange={e=>setForm({...form, gender: e.target.value})}>
            <option value="">Select</option>
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Blood Group</label>
          <input className="mg-input" value={form.blood_group} onChange={e=>setForm({...form, blood_group: e.target.value})} placeholder="e.g. O+" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">City</label>
          <input className="mg-input" value={form.city} onChange={e=>setForm({...form, city: e.target.value})} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Address</label>
          <input className="mg-input" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} />
        </div>
        {apiBase.startsWith("/clinic/") && <div>
          <label className="text-xs text-slate-500 mb-1 block">Emergency Contact Name</label>
          <input className="mg-input" value={form.emergency_contact_name} onChange={e=>setForm({...form, emergency_contact_name: e.target.value})} />
        </div>}
        {apiBase.startsWith("/clinic/") && <div>
          <label className="text-xs text-slate-500 mb-1 block">Emergency Contact Phone</label>
          <input className="mg-input" value={form.emergency_contact_phone} onChange={e=>setForm({...form, emergency_contact_phone: e.target.value})} />
        </div>}
        {apiBase.startsWith("/clinic/") && <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Allergies</label>
          <textarea className="mg-input w-full" rows={2} value={form.allergies_json} onChange={e=>setForm({...form, allergies_json: e.target.value})} placeholder="Record known allergies or leave blank" />
        </div>}
        {apiBase.startsWith("/clinic/") && <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Risk Information</label>
          <textarea className="mg-input w-full" rows={2} value={form.risk_flags_json} onChange={e=>setForm({...form, risk_flags_json: e.target.value})} placeholder="Record relevant clinical or operational risk flags" />
        </div>}
        {apiBase.startsWith("/clinic/") && <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Patient Tags</label>
          <PatientTagPicker value={tagIds} onChange={setTagIds} />
        </div>}
      </div>
    </CommonModal>
  );
}
